import Box from '@mui/material/Box';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { useFormContext } from 'react-hook-form';
import { useProviderStore } from '@repo/store/provider';
import {
  type AgreementAddRequestBodySchema,
  usePatchAgreementEdit,
  usePostAgreementAdd,
} from '@repo/api/endpoints/agreement';
import { areP24MarketingDataAccepted, getP24MarketingAgreements } from '@repo/store/helpers';
import some from 'lodash/some';
import { useEffect, useRef } from 'react';

import { marketingAgreementsDataToSend } from '@/components/welcome/components/p24-marketing-agreements-form/form/marketing-agreements-data-to-send';
import { P24MarketingAgreementsFormInputs } from '@/components/agreements/p24-marketing-agreements-form-inputs';
import { agreements } from '@/components/welcome/loan-application-possible/liberis/agreements-liberis/agreements';
import { Agreements } from '@/components/agreements/agreements';

interface Props {
  merchantResponseData: MerchantResponse;
  onRegisterMarketingSubmit?: (submit: () => void) => void;
}

export const LiberisModalAgreements = ({
  merchantResponseData,
  onRegisterMarketingSubmit,
}: Props) => {
  const { getValues } = useFormContext();
  const {
    state: { currentProvider },
  } = useProviderStore();
  const { mutate: postAgreementAdd } = usePostAgreementAdd();
  const { mutate: patchAgreementEdit } = usePatchAgreementEdit();

  const p24marketingAgreements = getP24MarketingAgreements(
    merchantResponseData.data.agreements?.marketing,
  );
  const displayMarketingAgreements = !areP24MarketingDataAccepted(p24marketingAgreements);

  // Persist the P24 marketing consents (email/sms/tel) like the welcome flow. They live
  // in the loan form's RHF state but are stripped by the resolver, so read via getValues.
  const submitMarketingAgreements = () => {
    if (!displayMarketingAgreements || !currentProvider) return;

    const data = {
      email: Boolean(getValues('email')),
      sms: Boolean(getValues('sms')),
      tel: Boolean(getValues('tel')),
    };

    const payload: AgreementAddRequestBodySchema = {
      requestId: merchantResponseData.requestId,
      apiProvider: 'liberis',
      agreements: {
        marketing: marketingAgreementsDataToSend(data, merchantResponseData),
      },
    };

    if (some(data, (value) => value)) {
      if (p24marketingAgreements && p24marketingAgreements.length > 0) {
        patchAgreementEdit(payload);
      } else {
        postAgreementAdd(payload);
      }
    }
  };

  // Keep the latest closure in a ref and register a stable wrapper once, so the host can
  // invoke it synchronously during submit (before it potentially unmounts this component).
  const submitMarketingRef = useRef(submitMarketingAgreements);
  submitMarketingRef.current = submitMarketingAgreements;

  useEffect(() => {
    onRegisterMarketingSubmit?.(() => submitMarketingRef.current());
  }, [onRegisterMarketingSubmit]);

  return (
    <>
      <Agreements
        isLiberisAgreements
        agreements={agreements}
        merchantResponseData={merchantResponseData}
        selectAllName="agreements.selectAll"
        includeP24MarketingAgreements={displayMarketingAgreements}
      />
      {displayMarketingAgreements ? (
        <Box marginTop={1} marginLeft="7px">
          <P24MarketingAgreementsFormInputs />
        </Box>
      ) : null}
    </>
  );
};
